import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const contactInfo = [
  {
    icon: 'ri-phone-line',
    title: 'Telefon',
    value: '0312 666 06 36',
    link: 'tel:+903126660636',
  },
  {
    icon: 'ri-mail-line',
    title: 'E-posta',
    value: 'info@yanginalgila.com',
    link: 'mailto:info@yanginalgila.com',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Adres',
    value: 'Konutkent Mahallesi 3028 Cadde Elmar Towers C Blok No:17 Çankaya / Ankara',
    link: null,
  },
];

export default function QuoteForm() {
  const ref = useRef<HTMLElement>(null);
  // `margin` is not a valid option for useInView – it should be `rootMargin`
  const isInView = useInView(ref, { once: true, rootMargin: '-80px' });

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    userEmail: '',
    company: '',
    serviceType: '',
    buildingType: '',
    area: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d67bjq6a728k8ctu1q80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          phone: '',
          userEmail: '',
          company: '',
          serviceType: '',
          buildingType: '',
          area: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-red-50" ref={ref} id="teklif-al">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-16 items-center"
        >
          {/* Left info */}
          <motion.div variants={fadeInLeft}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full mb-6">
              <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-red-700">Ücretsiz Teklif</span>
            </div>
            <h2 className="text-5xl font-bold leading-tight mb-6">
              <span className="text-gray-900">HİZMET </span>
              <span className="text-red-600">TEKLİFİ</span>
              <br />
              <span className="text-gray-900">ALIN</span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-10">
              İhtiyacınıza uygun yangın algılama sistemi için ücretsiz keşif ve teklif alın. Uzman ekibimiz
              mekanınızı inceleyerek en uygun çözümü sunar.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm">
                  <i className="ri-phone-line text-xl text-red-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Hemen Arayın</p>
                  <a
                    href="tel:+903126660636"
                    className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors cursor-pointer"
                  >
                    0312 666 06 36
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm">
                  <i className="ri-mail-line text-xl text-red-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-posta Gönderin</p>
                  <a
                    href="mailto:info@yanginalgila.com"
                    className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors cursor-pointer"
                  >
                    info@yanginalgila.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm">
                  <i className="ri-map-pin-line text-xl text-red-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Adresimiz</p>
                  <p className="text-lg font-bold text-gray-900">Konutkent Mah. 3028 Cad. Elmar Towers C Blok No:17</p>
                  <p className="text-sm text-gray-600">Çankaya / Ankara</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div variants={fadeInRight} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm">
            <form
              id="services-quote-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="sq-fullName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Ad Soyad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="sq-fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Adınız Soyadınız"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sq-phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Telefon <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="sq-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="05XX XXX XX XX"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="sq-userEmail"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    E-posta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="sq-userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleChange}
                    required
                    placeholder="ornek@email.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sq-company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Firma Adı
                  </label>
                  <input
                    type="text"
                    id="sq-company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Firma adınız"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="sq-serviceType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Hizmet Türü <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="sq-serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="Yangın Algılama Sistemi">Yangın Algılama Sistemi</option>
                    <option value="Duman Sensörleri">Duman Sensörleri</option>
                    <option value="Alarm Sistemleri">Alarm Sistemleri</option>
                    <option value="Komple Çözüm">Komple Çözüm</option>
                    <option value="Bakım ve Onarım">Bakım ve Onarım</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="sq-buildingType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Bina Türü
                  </label>
                  <select
                    id="sq-buildingType"
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="Ofis / İş Yeri">Ofis / İş Yeri</option>
                    <option value="Fabrika / Depo">Fabrika / Depo</option>
                    <option value="AVM / Mağaza">AVM / Mağaza</option>
                    <option value="Otel / Restoran">Otel / Restoran</option>
                    <option value="Hastane / Sağlık">Hastane / Sağlık</option>
                    <option value="Konut / Apartman">Konut / Apartman</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="sq-area"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Yaklaşık Alan (m²)
                </label>
                <input
                  type="text"
                  id="sq-area"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  placeholder="Örn: 500"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label
                  htmlFor="sq-message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Ek Bilgi
                </label>
                <textarea
                  id="sq-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  placeholder="Projeniz hakkında detay paylaşın..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">Talebiniz alındı!</p>
                    <p className="text-xs text-green-600">
                      En kısa sürede sizinle iletişime geçeceğiz.
                    </p>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
                    <i className="ri-error-warning-line text-red-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-800">Bir hata oluştu!</p>
                    <p className="text-xs text-red-600">Lütfen tekrar deneyin.</p>
                  </div>
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> Ücretsiz Teklif Alın
                  </>
                )}
              </motion.button>

              <p className="text-xs text-gray-500 text-center">
                <i className="ri-shield-check-line text-green-500 mr-1"></i>
                Bilgileriniz güvenle saklanır ve üçüncü şahıslarla paylaşılmaz.
              </p>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
